import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchDeleteClasse } from "../../app/classes/classes.slice";
import { useHistory } from "react-router";
import dayjs from "dayjs";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const Classes = () => {
  const { t } = useTranslation();
  const { classes } = useSelector((state) => state);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const dispatch = useDispatch();
  const history = useHistory();

  const handleDeleteSelect = (item) => {
    setModalDeleteOpen(true);
    setSelectedItem(item);
  };

  const handleDelete = () => {
    dispatch(fetchDeleteClasse(selectedItem));
    setModalDeleteOpen(false);
    setSelectedItem(null);
  };

  const classList = classes?.value || [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Paper sx={{ borderRadius: 1, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <Box sx={{ p: 3, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h6" fontWeight={700}>{t("pages.classes")}</Typography>
            <Chip label={classList.length} size="small" sx={{ fontWeight: 700, fontSize: 13 }} />
          </Stack>
          <Button variant="contained" onClick={() => history.push("/classes/add")}>
            {t("pages.newClass")}
          </Button>
        </Box>

        {classes.isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : classList.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">{t("classes.noClassesYet")}</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("classes.title")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("classes.students")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("classes.lastEdited")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }} align="right">{t("common.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {classList.map((item, index) => (
                  <TableRow key={item?._id || index} hover sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{item?.title}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{item?.students?.length || 0}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item?.updatedAt ? dayjs(item.updatedAt).format("DD MMM YYYY") : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton size="small" color="primary" onClick={() => history.push(`/classes/edit/${item?._id}`)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteSelect(item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={modalDeleteOpen} onClose={() => { setSelectedItem(null); setModalDeleteOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t("common.delete")} {t("pages.classes").toLowerCase()}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this class?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setSelectedItem(null); setModalDeleteOpen(false); }}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Classes;
